// AnchorSyncController (TR7, PRD-F5).
//
// Consolidates the two anchor-sync paths previously living in
// `o2d-whiteboard.element.ts`:
//   - Live path  : `bus.uiActions$` `drag/move` → Konva-only mutation
//   - Commit path: `bus.domain$` `object-updated` → `state.replaceObject`
//
// T10 implements the live path; T11 will add the commit path.
//
// Stefan, 2026-08-31 — a THIRD path was added: `applyLiveResize`, a public
// method the host (`o2d-whiteboard.element.ts`) calls IMPERATIVELY from
// `onTransformLive`, instead of going through the bus like the two paths
// above. This is deliberate, not an oversight: a resize is not a
// translation (see the method's own doc), so it cannot be expressed as a
// `drag/move` UiAction without losing the live width/height — there is no
// bus event shape today that carries a live resize. The host therefore
// holds a direct reference to this controller (`private anchorSync?:
// AnchorSyncController` there) for this one call, breaking the "the two
// paths above never need a direct reference" decoupling this class was
// originally documented as providing. Kept as a public method (not folded
// into the bus) so it stays trivially unit-testable in isolation, exactly
// like the two paths above.

import type Konva from 'konva';
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { Subject, filter, takeUntil } from 'rxjs';
import { withPatch } from '@o2d/shared';

import type { BoardObject } from '../interfaces/board-object.interface.js';
import type { LineObject } from '../../features/line/line.interface.js';
import type { BoardState } from '../state/board-state.js';
import type { WhiteboardBus } from '../bus/whiteboard-bus.js';
import type { UiAction } from '../bus/ui-action.events.js';
import type { DomainEvent } from '../bus/domain-events.js';
import { resolveAnchor, type AnchorTarget, type AnchorBox } from '../../objects/line-anchor.js';

/**
 * Konva-related dependencies the controller needs to mutate the live drag
 * preview without going through `state.replaceObject`. Held by the host
 * (`o2d-whiteboard.element.ts`) and passed in by reference so registration
 * timing does not matter (TR7).
 */
export interface AnchorSyncDeps {
  konvaNodeFor(id: string): Konva.Node | undefined;
  /**
   * Repaint a line's Konva node from a transient object WITHOUT touching
   * BoardState — the same routine (`onObjectUpdated`) a committed move flows
   * through. Used for the live anchor-follow preview so the stroke AND the end
   * caps land at the moved endpoint on every frame; hand-mutating the group
   * here would repaint the stroke but leave the caps at their stale position.
   */
  renderLine(line: LineObject): void;
}

type DragMoveAction = Extract<UiAction, { type: 'drag/move' }>;
type ObjectUpdatedEvent = Extract<DomainEvent, { type: 'object-updated' }>;

export class AnchorSyncController implements ReactiveController {
  readonly #host: ReactiveControllerHost;
  readonly #bus: WhiteboardBus;
  readonly #state: BoardState;
  readonly #deps: AnchorSyncDeps;
  readonly #isAnchorTarget: (obj: BoardObject | null | undefined) => boolean;
  readonly #anchorBounds: (obj: AnchorTarget) => AnchorBox;
  #disconnect$: Subject<void> | null = null;

  constructor(
    host: ReactiveControllerHost,
    bus: WhiteboardBus,
    state: BoardState,
    deps: AnchorSyncDeps,
    isAnchorTarget: (obj: BoardObject | null | undefined) => boolean,
    anchorBounds: (obj: AnchorTarget) => AnchorBox,
  ) {
    this.#host = host;
    this.#bus = bus;
    this.#state = state;
    this.#deps = deps;
    this.#isAnchorTarget = isAnchorTarget;
    this.#anchorBounds = anchorBounds;
    host.addController(this);
  }

  hostConnected(): void {
    void this.#host;
    this.#disconnect$ = new Subject<void>();
    const stop$ = this.#disconnect$;
    this.#bus.uiActions$
      .pipe(
        filter((a): a is DragMoveAction => a.type === 'drag/move' && a.targetId !== undefined),
        takeUntil(stop$),
        takeUntil(this.#bus.destroy$),
      )
      .subscribe({
        next: (action) => this.#applyLive(action.targetId as string, action.worldX, action.worldY),
        error: () => {
          if (import.meta.env?.DEV) {
             
            console.warn('[AnchorSyncController] live-path bus error swallowed');
          }
        },
      });
    this.#bus.domain$
      .pipe(
        filter(
          (e): e is ObjectUpdatedEvent =>
            e.type === 'object-updated' && e.payload.type !== 'line',
        ),
        takeUntil(stop$),
        takeUntil(this.#bus.destroy$),
      )
      .subscribe({
        next: (event) => this.#applyCommit(event.payload),
        error: () => {
          if (import.meta.env?.DEV) {

            console.warn('[AnchorSyncController] commit-path bus error swallowed');
          }
        },
      });
  }

  hostDisconnected(): void {
    this.#disconnect$?.next();
    this.#disconnect$?.complete();
    this.#disconnect$ = null;
  }

  /**
   * Repaint anchored lines to follow the live drag WITHOUT writing to
   * BoardState. Only the GRABBED node emits a `drag/move`, but in a group drag
   * every co-moving object shifts by the SAME delta. We derive that delta from
   * the grabbed target (`worldX/Y` − its committed position) and apply it to
   * EVERY co-moving anchor target — so a line docked to a non-grabbed but
   * co-moving note (e.g. a connector between two selected notes) follows on both
   * ends, not just the grabbed one.
   */
  #applyLive(targetId: string, worldX: number, worldY: number): void {
    const target = this.#state.getObject(targetId);
    if (!target || !this.#isAnchorTarget(target)) return;

    // Live drag delta of the grabbed target; shared by all co-moving siblings.
    const dx = worldX - target.x;
    const dy = worldY - target.y;

    // Targets moving together in this gesture. A plain node drag co-moves the
    // whole multi-selection rigidly, so the selection IS the co-moving set. A
    // single drag has no siblings (only the grabbed target moves).
    const selection = this.#bus.selection$.value;
    const coMoving = selection.length > 1 ? new Set<string>(selection) : null;
    const isMoving = (id: string): boolean => id === targetId || (coMoving?.has(id) ?? false);

    // Anchor box of a co-moving target at its LIVE (committed + delta) position.
    const liveBoxFor = (id: string): AnchorBox | null => {
      const obj = this.#state.getObject(id);
      if (!obj || !this.#isAnchorTarget(obj)) return null;
      return this.#anchorBounds({ ...obj, x: obj.x + dx, y: obj.y + dy } as AnchorTarget);
    };

    for (const candidate of this.#state.allObjects()) {
      if (candidate.type !== 'line') continue;
      const line = candidate as LineObject;

      // A line that is ITSELF part of the multi-selection is co-moved rigidly by
      // the element (its Konva node is offset by the drag delta). Repainting it
      // here too would move its anchored endpoint twice — the live-preview mirror
      // of the commit-path double-move — so leave it to the rigid co-move.
      if (coMoving?.has(line.id)) continue;

      const startMoving = !!line.startAnchor && isMoving(line.startAnchor.objectId);
      const endMoving = !!line.endAnchor && isMoving(line.endAnchor.objectId);
      if (!startMoving && !endMoving) continue;

      // Skip lines with no rendered node — nothing to preview, and no point
      // building a transient object the renderer would drop on the floor.
      if (!this.#deps.konvaNodeFor(line.id)) continue;

      // Re-derive ONLY the endpoints whose target is moving; a free or
      // non-moving-anchored endpoint keeps its stored coordinates.
      let { x1, y1, x2, y2 } = line;
      if (startMoving && line.startAnchor) {
        const box = liveBoxFor(line.startAnchor.objectId);
        if (box) {
          const p = resolveAnchor(line.startAnchor, box);
          x1 = p.x;
          y1 = p.y;
        }
      }
      if (endMoving && line.endAnchor) {
        const box = liveBoxFor(line.endAnchor.objectId);
        if (box) {
          const p = resolveAnchor(line.endAnchor, box);
          x2 = p.x;
          y2 = p.y;
        }
      }

      // Hand the moved endpoints AND their bbox to the shared render routine.
      // It sets the group frame (applyBaseGeometry) and rebuilds stroke + caps
      // in that frame, so the whole line — caps included — follows every frame.
      this.#deps.renderLine({
        ...line,
        x1,
        y1,
        x2,
        y2,
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
      });
    }
  }

  /**
   * Repaint anchored lines to follow a live RESIZE of `targetId` — its box now
   * differs in width/height (and possibly x/y, depending on which corner is
   * dragged) from the committed state. Deliberately separate from
   * `#applyLive` (the `drag/move` UiAction path): that path models a pure
   * TRANSLATION — it derives a uniform (dx, dy) and applies it to every
   * co-moving anchor target's STORED width/height. A resize is not a
   * translation: the target's own width/height are what changed, and
   * treating it as a `drag/move` with the transformer's live x/y left docked
   * line endpoints frozen at the pre-resize box for the whole gesture (they
   * only caught up once `state.replaceObject` landed on release) — a resize
   * that keeps a corner fixed (e.g. dragging the bottom-right handle) even
   * produces `worldX === target.x`, i.e. a zero delta, so `#applyLive` had
   * literally nothing to move. Resize has no multi-select live path (see
   * `onTransformLive`'s `nodes().length > 1` guard in the host), so this only
   * ever concerns the single resized target — no co-moving siblings to derive.
   */
  applyLiveResize(targetId: string, liveX: number, liveY: number, liveWidth: number, liveHeight: number): void {
    const target = this.#state.getObject(targetId);
    if (!target || !this.#isAnchorTarget(target)) return;

    const box = this.#anchorBounds({
      ...target,
      x: liveX,
      y: liveY,
      width: liveWidth,
      height: liveHeight,
    } as AnchorTarget);

    for (const candidate of this.#state.allObjects()) {
      if (candidate.type !== 'line') continue;
      const line = candidate as LineObject;
      if (!this.#deps.konvaNodeFor(line.id)) continue;

      let { x1, y1, x2, y2 } = line;
      let changed = false;
      if (line.startAnchor?.objectId === targetId) {
        const p = resolveAnchor(line.startAnchor, box);
        x1 = p.x;
        y1 = p.y;
        changed = true;
      }
      if (line.endAnchor?.objectId === targetId) {
        const p = resolveAnchor(line.endAnchor, box);
        x2 = p.x;
        y2 = p.y;
        changed = true;
      }
      if (!changed) continue;

      this.#deps.renderLine({
        ...line,
        x1,
        y1,
        x2,
        y2,
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
      });
    }
  }

  /**
   * Reconcile attached lines after an anchor target was committed via
   * `state.replaceObject`. Walks all lines, recomputes endpoint coords for
   * any line whose start/end anchor references the moved target, and writes
   * the result back through `state.replaceObject` (with `withPatch`).
   *
   * The DomainEvent stream is already pre-filtered to `payload.type !== 'line'`
   * (FC15 anti-reentrancy) — this method then additionally gates on
   * `isAnchorTarget(target)` so non-anchorable types (e.g. `mystery`) are no-ops.
   */
  #applyCommit(target: BoardObject): void {
    if (!this.#isAnchorTarget(target)) return;
    const box = this.#anchorBounds(target as AnchorTarget);

    for (const candidate of this.#state.allObjects()) {
      if (candidate.type !== 'line') continue;
      const line = candidate as LineObject;
      let { x1, y1, x2, y2 } = line;
      let changed = false;
      if (line.startAnchor && line.startAnchor.objectId === target.id) {
        const p = resolveAnchor(line.startAnchor, box);
        x1 = p.x;
        y1 = p.y;
        changed = true;
      }
      if (line.endAnchor && line.endAnchor.objectId === target.id) {
        const p = resolveAnchor(line.endAnchor, box);
        x2 = p.x;
        y2 = p.y;
        changed = true;
      }
      if (!changed) continue;

      this.#state.replaceObject(
        line.id,
        withPatch(line, {
          x1,
          y1,
          x2,
          y2,
          x: Math.min(x1, x2),
          y: Math.min(y1, y2),
          width: Math.abs(x2 - x1),
          height: Math.abs(y2 - y1),
        }),
      );
    }
  }
}
